import type { FieldErrors } from 'react-hook-form';
import * as z from 'zod';
import {
  MAX_FAQS,
  MAX_FAQ_ANSWER_LENGTH,
  MAX_FAQ_QUESTION_LENGTH,
} from 'constants/payloads/venues/faqs';
import type { VenueFaq } from '../../types';

/** Max FAQs a club can publish — shared with the API route. */
export { MAX_FAQS };

export const faqItemSchema = z.object({
  question: z
    .string()
    .trim()
    .min(1, { message: 'Question is required' })
    .max(MAX_FAQ_QUESTION_LENGTH, {
      message: `Questions can be up to ${MAX_FAQ_QUESTION_LENGTH} characters`,
    }),
  answer: z
    .string()
    .trim()
    .min(1, { message: 'Answer is required' })
    .max(MAX_FAQ_ANSWER_LENGTH, {
      message: `Answers can be up to ${MAX_FAQ_ANSWER_LENGTH} characters`,
    }),
});

export const faqFormSchema = z.object({
  faqs: z.array(faqItemSchema).max(MAX_FAQS, {
    message: `You can add up to ${MAX_FAQS} questions`,
  }),
});

export type FaqFormValues = z.infer<typeof faqFormSchema>;

export const EMPTY_FAQ = { question: '', answer: '' };

/** Builds RHF default values from the venue's stored FAQs (one empty row if none). */
export const toFaqFormValues = (faqs: VenueFaq[]): FaqFormValues => ({
  faqs: faqs.length > 0 ? faqs.map(({ question, answer }) => ({ question, answer })) : [EMPTY_FAQ],
});

/**
 * Flattens RHF field-array errors into the flat `faqs.<i>.<field>` keys that
 * `InputField` looks up (it reads `errors[name]`, not the nested tree).
 */
export const flattenFaqErrors = (errors: FieldErrors<FaqFormValues>): FieldErrors => {
  const flat: Record<string, { message?: string }> = {};
  const faqErrors = errors.faqs;
  if (Array.isArray(faqErrors)) {
    faqErrors.forEach((rowError, index) => {
      if (rowError?.question) flat[`faqs.${index}.question`] = rowError.question;
      if (rowError?.answer) flat[`faqs.${index}.answer`] = rowError.answer;
    });
  }
  return flat as FieldErrors;
};
